import { useEffect, useMemo, useState } from 'react';
import { BellRing, Loader2 } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import { vzhledModulu } from '../../lib/notifikace';

const AKCENT = '#FF8A00';

/** Tři stavy, jak upozornění chodí. */
const VOLBY = [
    { id: 'vse', label: 'Telefon' },
    { id: 'tise', label: 'Jen v aplikaci' },
    { id: 'vypnuto', label: 'Vypnout' },
];

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

/**
 * Upozornění podle uživatele (2026-10-23) — každý si u druhu notifikace
 * nastaví, jestli mu chodí na telefon (a do aplikace), jen do aplikace, nebo
 * vůbec. Notifikace k vyřízení (schválit, podepsat) jdou nejvýš ztišit —
 * jinak by se věc, na kterou se čeká, nikdy nevyřídila.
 *
 * Nahoře jen druhy, které uživateli za poslední půlrok opravdu přišly; zbytek
 * katalogu (70 druhů) je na rozbalení, ať první pohled není nekonečný seznam.
 * Ukládá se hned po ťuknutí.
 */
export default function NastaveniUpozorneni({ open, onClose, isDark }) {
    const [druhy, setDruhy] = useState(null);
    const [vse, setVse] = useState(false);
    const [chyba, setChyba] = useState(null);
    const [uklada, setUklada] = useState(null);

    useEffect(() => {
        if (!open) return;
        setChyba(null);
        fetch('/api/me/notifikace-nastaveni', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
            .then((r) => r.json())
            .then((d) => setDruhy(d.druhy ?? []))
            .catch(() => setChyba('Nastavení se nepodařilo načíst.'));
    }, [open]);

    const zmenit = async (druh, rezim) => {
        const predtim = druhy;
        setUklada(druh);
        setDruhy((ds) => ds.map((d) => (d.druh === druh ? { ...d, rezim } : d)));
        try {
            const r = await fetch('/api/me/notifikace-nastaveni', {
                method: 'PUT', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({ druh, rezim }),
            });
            if (!r.ok) throw new Error();
            const d = await r.json();
            setDruhy((ds) => ds.map((x) => (x.druh === druh ? { ...x, rezim: d.rezim } : x)));
        } catch {
            setDruhy(predtim);
            setChyba('Uložení se nepovedlo, zkus to znovu.');
        }
        setUklada(null);
    };

    const skupiny = useMemo(() => {
        const vybrane = (druhy ?? []).filter((d) => vse || d.dostava || d.rezim !== 'vse');
        const mapa = new Map();
        for (const d of vybrane) {
            if (!mapa.has(d.modul)) mapa.set(d.modul, []);
            mapa.get(d.modul).push(d);
        }
        return [...mapa.entries()];
    }, [druhy, vse]);

    const skryto = (druhy ?? []).length - skupiny.reduce((s, [, ds]) => s + ds.length, 0);
    const muted = isDark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
    const hrana = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';

    return (
        <ModalShell open={open} onClose={onClose} isDark={isDark} ikona={BellRing} barva={AKCENT} nadpis="Nastavit upozornění" sirka={560}>
            <div className="px-4 py-3 flex flex-col gap-4">
                <p className="text-xs" style={{ color: muted }}>
                    U každého upozornění si vyber, jak ti má chodit. Věci k vyřízení (schválit, podepsat) jdou jen ztišit.
                </p>
                {chyba && <p className="text-xs" style={{ color: '#EF4444' }}>{chyba}</p>}
                {!druhy && !chyba && (
                    <div className="flex items-center gap-2 text-sm" style={{ color: muted }}><Loader2 size={14} className="animate-spin" /> Načítám…</div>
                )}
                {druhy && skupiny.length === 0 && (
                    <p className="text-sm" style={{ color: muted }}>Zatím ti žádná upozornění nepřišla.</p>
                )}
                {skupiny.map(([modul, ds]) => {
                    const v = vzhledModulu(modul);
                    const Ikona = v.Icon;
                    return (
                        <div key={modul}>
                            <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-semibold uppercase tracking-wide" style={{ color: v.color }}>
                                {Ikona && <Ikona size={13} />} {v.label}
                            </div>
                            <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${hrana}` }}>
                                {ds.map((d, i) => (
                                    <div key={d.druh} className="flex flex-wrap items-center gap-2 px-3 py-2.5"
                                        style={{ borderTop: i ? `1px solid ${hrana}` : 'none' }}>
                                        <span className="flex-1 min-w-[140px] text-sm" style={{ color: 'var(--text-primary)' }}>
                                            {d.nazev}
                                            {d.akcni && <span className="block text-[11px]" style={{ color: muted }}>k vyřízení</span>}
                                        </span>
                                        <div className="flex rounded-lg p-0.5 gap-0.5" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', opacity: uklada === d.druh ? 0.6 : 1 }}>
                                            {VOLBY.filter((o) => !(o.id === 'vypnuto' && d.akcni)).map((o) => {
                                                const on = d.rezim === o.id;
                                                return (
                                                    <button key={o.id} type="button" onClick={() => !on && zmenit(d.druh, o.id)}
                                                        className="rounded-md px-2.5 text-xs font-semibold"
                                                        style={{
                                                            height: 32, border: 'none', cursor: 'pointer',
                                                            background: on ? (o.id === 'vypnuto' ? 'rgba(239,68,68,0.16)' : `${AKCENT}26`) : 'transparent',
                                                            color: on ? (o.id === 'vypnuto' ? '#EF4444' : AKCENT) : muted,
                                                        }}>
                                                        {o.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
                {druhy && (skryto > 0 || vse) && (
                    <button type="button" onClick={() => setVse((x) => !x)}
                        className="self-start text-xs font-semibold" style={{ background: 'none', border: 'none', color: AKCENT, cursor: 'pointer', padding: 0 }}>
                        {vse ? 'Jen ta, která mi chodí' : `Zobrazit všechna upozornění (+${skryto})`}
                    </button>
                )}
            </div>
        </ModalShell>
    );
}
